// Realtime client (doc 08 §8): one socket per browser tab, shared by every live widget.
import type { LiveItem, SubscribeAck } from '@twinlink/shared';
import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getAccessToken, refreshAccessToken } from './api';
import { useAuth } from './auth';

type Listener = (items: LiveItem[]) => void;
interface Sub {
  signalIds: string[];
  displayUnits: Record<string, string>;
  listener: Listener;
  onDenied?: (denied: string[] | 'all') => void;
}

let socket: Socket | null = null;
let joinedOrg: string | null = null;
let seq = 0;
const subs = new Map<string, Sub>();
const dashboardListeners = new Set<(e: { dashboardId: string; version: number }) => void>();
const alertListeners = new Set<() => void>();

function send(subId: string, sub: Sub): void {
  socket?.emit('subscribe', { subId, signalIds: sub.signalIds, displayUnits: sub.displayUnits }, (ack: SubscribeAck) => {
    if (ack.denied.length > 0) sub.onDenied?.(ack.denied);
  });
}

function ensureSocket(orgId: string): Socket {
  if (!socket) {
    socket = io('/live', {
      path: '/socket.io',
      transports: ['websocket'],
      // A function, so every (re)connection uses the current access token.
      auth: (cb) => cb({ token: getAccessToken() }),
      reconnectionDelayMax: 10_000,
    });
    socket.on('connect', () => {
      // After a reconnect the server has forgotten this socket: join and subscribe again.
      if (joinedOrg) socket!.emit('org:join', { orgId: joinedOrg }, () => subs.forEach((sub, id) => send(id, sub)));
    });
    socket.on('connect_error', (err) => {
      // The access token expired while disconnected: get a new one, the socket retries by itself.
      if (err.message === 'UNAUTHENTICATED') void refreshAccessToken();
    });
    socket.on('values', (payload: { subId: string; items: LiveItem[] }) => subs.get(payload.subId)?.listener(payload.items));
    socket.on('access:changed', (payload: { subId: string; denied: string[] | 'all' }) => subs.get(payload.subId)?.onDenied?.(payload.denied));
    socket.on('dashboard:changed', (e: { dashboardId: string; version: number }) => dashboardListeners.forEach((l) => l(e)));
    socket.on('alerts:changed', () => alertListeners.forEach((l) => l()));
    // Suspended, signed out elsewhere, password changed: this tab is logged out too.
    socket.on('session:revoked', () => useAuth.getState().clear());
  }
  if (joinedOrg !== orgId) {
    joinedOrg = orgId;
    subs.clear();
    if (socket.connected) socket.emit('org:join', { orgId });
  }
  return socket;
}

/** Calls back when an alert of the organization opens or clears. Returns the unsubscribe. */
export function onAlertsChanged(orgId: string, listener: () => void): () => void {
  ensureSocket(orgId);
  alertListeners.add(listener);
  return () => void alertListeners.delete(listener);
}

export function closeLive(): void {
  socket?.disconnect();
  socket = null;
  joinedOrg = null;
  subs.clear();
}

function subscribe(orgId: string, sub: Sub): () => void {
  ensureSocket(orgId);
  const subId = `s${++seq}`;
  subs.set(subId, sub);
  if (socket?.connected) send(subId, sub);
  return () => {
    subs.delete(subId);
    socket?.emit('unsubscribe', { subId });
  };
}

/**
 * Live last values for a set of signals: one socket subscription for the whole set. Updates
 * are merged into one map, so a message touching three signals renders once.
 */
export function useLiveValues(orgId: string, signalIds: string[], displayUnits: Record<string, string> = {}, enabled = true): { values: Map<string, LiveItem>; denied: Set<string> } {
  const [values, setValues] = useState<Map<string, LiveItem>>(new Map());
  const [denied, setDenied] = useState<Set<string>>(new Set());
  const key = `${orgId}|${[...signalIds].sort().join(',')}|${JSON.stringify(displayUnits)}`;
  const latest = useRef({ signalIds, displayUnits });
  latest.current = { signalIds, displayUnits };

  useEffect(() => {
    setValues(new Map());
    setDenied(new Set());
    if (!enabled || latest.current.signalIds.length === 0) return;
    return subscribe(orgId, {
      signalIds: latest.current.signalIds,
      displayUnits: latest.current.displayUnits,
      listener: (items) =>
        setValues((prev) => {
          const next = new Map(prev);
          for (const item of items) next.set(item.signalId, item);
          return next;
        }),
      onDenied: (ids) => {
        const lost = ids === 'all' ? latest.current.signalIds : ids;
        setDenied((prev) => new Set([...prev, ...lost]));
        // Values of signals the user may no longer see disappear at once.
        setValues((prev) => {
          const next = new Map(prev);
          for (const id of lost) next.delete(id);
          return next;
        });
      },
    });
    // `key` captures the signal set and units.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { values, denied };
}

/** Calls back when someone saves the dashboard with this id. */
export function useDashboardChanged(orgId: string, dashboardId: string | undefined, onChange: (version: number) => void): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    if (!dashboardId) return;
    ensureSocket(orgId);
    const listener = (e: { dashboardId: string; version: number }) => {
      if (e.dashboardId === dashboardId) cb.current(e.version);
    };
    dashboardListeners.add(listener);
    return () => void dashboardListeners.delete(listener);
  }, [orgId, dashboardId]);
}
